import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { appCurrentBranding } from '../../../../config/branding';
import { branding, chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/reports';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Energy report trend chart
 * This component displays the energy consumption trend over time.
 */
function RevenueReportTrendChart() {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const reportsData = useAppSelector((state) => state.reportData);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        displayColors: false,
        callbacks: {
          /**
           * Returns the tooltip title.
           * @returns The tooltip title string.
           */
          title: () => t('totalRevenue'),
          /**
           * Formats the tooltip label using locale string.
           * @param context - The tooltip context object.
           * @returns The formatted label string.
           */
          label: (context: any) =>
            `${context.raw.toLocaleString()}  ${appCurrentBranding === branding.ENERGIC ? 'SAR' : 'USD'}`,
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
    },
  };

  const primaryColor = getComputedStyle(document.documentElement)
  .getPropertyValue('--primary-color')
  .trim();

  const verticalHoverLinePlugin = {
  id: 'verticalHoverLine',
  /**
     * Draws a black vertical crosshair line at the hovered x position.
     * @param chart - The Chart.js instance
     */
   afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        const successDark = getComputedStyle(document.documentElement)
          .getPropertyValue('--success')
          .trim();

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = successDark;
        context.stroke();
        context.restore();
      }
    },
};

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param range - The time range for the report ('last24hours', 'last7days', 'lastMonth', 'last12months').
   */
  const fetchtotalRevenueReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'totalRevenue',
        accountCustomId: reportsData?.accountId,
      });

      if (response.status === 200) {
        const { data } = response;
        switch (range) {
          case 'last12months':
            setBarGraphData(fillMissingMonthsGeneric(data));
            break;
          case 'lastMonth':
            setBarGraphData(fillMissingLastNDaysGeneric(data, 30));
            break;
          case 'last7days':
            setBarGraphData(fillMissingLastNDaysGeneric(data, 7));
            break;
          case 'last24hours':
            setBarGraphData(fillMissingLast24HrsGeneric(data));
            break;
          default:
            setBarGraphData([]);
            break;
        }
      }
    } catch (error) {
      console.error('Failed to fetch energy report data:', error);
    }
  };
  /**
   * If clicking the already active pin, deactivate it
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchtotalRevenueReport('last24hours');
  }, []);

  const labels = barGraphData.map((d) => d.month || d.label);
  const values = barGraphData.map((d) => d.totalRevenue || 0);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('totalRevenue') ? 'active' : ''}`}
              onClick={() => handleClick('totalRevenue')}>
              {reportsData.activePinName.includes('totalRevenue') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('totalRevenueTrend')}
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((item) => item.label)}
            onToggle={(_, label) => {
              const selected = chartTimePeriod.find((item) => item.label === label);
              if (selected) fetchtotalRevenueReport(selected.value);
            }}
            defaultActiveIndex={0}
            type="chart"
          />
        </div>

        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <Line
             plugins={[verticalHoverLinePlugin]}
              data={{
                labels,
                datasets: [
                  {
                    label:t('powerConsumed'),
                    data: values,
                    borderColor: primaryColor,
                    backgroundColor: primaryColor,
                    fill: false,
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointBackgroundColor: primaryColor,
                    pointBorderColor: primaryColor,
                  },
                ],
              }}
              options={chartOptions}
            />
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default RevenueReportTrendChart;
